import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { forkJoin, Observable } from 'rxjs';
import { environment } from '@env/environment';
import { Page } from '@interfaces/page';
import { SortingPagingData } from '@helpers/sorting-paging-data';
import { BankStatementImportRun, BankStatementMatchRun, ListBankStatement } from '../_models/list-bank-statement.model';
import { AccountingDocumentForMatch, BankStatementMatch } from '../_models/bank-statement-match.model';

const apiServerUrl          = environment.apiBaseURL;
const baseUrl               = apiServerUrl + '/bank-statement';
const findPageableUrl       = baseUrl + '/pageable';
const countPageableUrl      = baseUrl + '/pageable/count';
const matchesUrl            = baseUrl + '/{id}/matches';
const documentsForMatchUrl  = baseUrl + '/documents-for-matching';
const documentsForMatchCountUrl = baseUrl + '/documents-for-matching/count';
const linkUrl               = baseUrl + '/{id}/match/{documentId}';
const unlinkUrl             = baseUrl + '/match/{matchId}/unlink';
const importRecentUrl       = baseUrl + '/import-recent';
const rematchUrl            = baseUrl + '/rematch';
const paymentTypesUrl       = baseUrl + '/payment-types';

@Injectable({
  providedIn: 'root'
})
export class BankStatementService {

  constructor(private http: HttpClient) { }

  /**
   * The rows and their total, fetched together. Two calls because the paged response
   * carries only fromRow/toRow - the total lives behind its own endpoint.
   */
  public pageable(sortingPaging: SortingPagingData, filters: any,
                  dateFrom: string, dateTo: string): Observable<[Page<ListBankStatement>, number]> {
    return forkJoin([
      this.findPageable(sortingPaging, filters, dateFrom, dateTo),
      this.countAll(filters, dateFrom, dateTo)
    ]);
  }

  public findPageable(sortingPaging: SortingPagingData, filters: any,
                      dateFrom: string, dateTo: string): Observable<Page<ListBankStatement>> {
    let params = new HttpParams()
      .set('page', sortingPaging.pageNumber.toString())
      .set('pageSize', sortingPaging.pageSize.toString());

    if (sortingPaging.isSortingValid()) {
      params = params
        .set('sortBy', sortingPaging.sortBy)
        .set('sortAsc', sortingPaging.sortAsc ? 'true' : 'false');
    }

    params = this.applyFilters(params, filters, dateFrom, dateTo);

    return this.http.get<Page<ListBankStatement>>(findPageableUrl, { params });
  }

  public countAll(filters: any, dateFrom: string, dateTo: string): Observable<number> {
    // Deliberately without the paging and sorting params - they cannot change the total,
    // and sending them would only invite the two queries to disagree.
    return this.http.get<number>(countPageableUrl,
      { params: this.applyFilters(new HttpParams(), filters, dateFrom, dateTo) });
  }

  /**
   * The payment types the filter dropdown offers, alphabetically, as they exist in the data.
   *
   * These are the bank's own descriptions and already in Bulgarian, so they are shown verbatim
   * rather than through a translation key - there is nothing here for us to have translated.
   */
  public findPaymentTypes(): Observable<string[]> {
    return this.http.get<string[]>(paymentTypesUrl);
  }

  /** The invoices this statement is currently linked to. */
  public findMatches(statementId: number): Observable<BankStatementMatch[]> {
    return this.http.get<BankStatementMatch[]>(matchesUrl.replace('{id}', `${statementId}`));
  }

  /** Candidate invoices for the matching dialog, paged like every other search here. */
  public searchDocuments(sortingPaging: SortingPagingData, filters: any,
                         dateFrom: string, dateTo: string): Observable<[Page<AccountingDocumentForMatch>, number]> {
    return forkJoin([
      this.findDocumentsPageable(sortingPaging, filters, dateFrom, dateTo),
      this.countDocuments(filters, dateFrom, dateTo)
    ]);
  }

  public findDocumentsPageable(sortingPaging: SortingPagingData, filters: any,
                               dateFrom: string, dateTo: string): Observable<Page<AccountingDocumentForMatch>> {
    let params = new HttpParams()
      .set('page', sortingPaging.pageNumber.toString())
      .set('pageSize', sortingPaging.pageSize.toString());

    if (sortingPaging.isSortingValid()) {
      params = params
        .set('sortBy', sortingPaging.sortBy)
        .set('sortAsc', sortingPaging.sortAsc ? 'true' : 'false');
    }

    return this.http.get<Page<AccountingDocumentForMatch>>(documentsForMatchUrl,
      { params: this.applyDocumentFilters(params, filters, dateFrom, dateTo) });
  }

  public countDocuments(filters: any, dateFrom: string, dateTo: string): Observable<number> {
    return this.http.get<number>(documentsForMatchCountUrl,
      { params: this.applyDocumentFilters(new HttpParams(), filters, dateFrom, dateTo) });
  }

  /** Links an invoice to the statement; the backend settles it and returns the document id. */
  public link(statementId: number, accountingDocumentId: number): Observable<number> {
    return this.http.post<number>(
      linkUrl.replace('{id}', `${statementId}`).replace('{documentId}', `${accountingDocumentId}`), null);
  }

  /** Undoes one link. Addressed by the MATCH id, not the document id. */
  public unlink(matchId: number): Observable<void> {
    return this.http.post<void>(unlinkUrl.replace('{matchId}', `${matchId}`), null);
  }

  /**
   * Fetches the last 30 days from the bank by hand, for when the nightly import did not.
   * Slow - it calls the bank - so the caller must keep its button disabled until it returns.
   */
  public importRecent(): Observable<BankStatementImportRun> {
    return this.http.post<BankStatementImportRun>(importRecentUrl, null);
  }

  /** Re-runs the matcher over the recent statements that are still unresolved. */
  public rematchRecent(): Observable<BankStatementMatchRun> {
    return this.http.post<BankStatementMatchRun>(rematchUrl, null);
  }

  private applyDocumentFilters(params: HttpParams, filters: any, dateFrom: string, dateTo: string): HttpParams {
    if (filters?.invoiceNumber)     params = params.set('invoiceNumber', filters.invoiceNumber);
    if (filters?.recipientFullName) params = params.set('recipientFullName', filters.recipientFullName);
    if (dateFrom)                   params = params.set('dateFrom', dateFrom);
    if (dateTo)                     params = params.set('dateTo', dateTo);

    // Guarded on null/'' rather than on truthiness, as on the list screen - a zero total is
    // an odd invoice but a real one, and a plain if would drop it silently.
    if (filters?.totalAmount !== null && filters?.totalAmount !== undefined && filters?.totalAmount !== '') {
      params = params.set('totalAmount', `${filters.totalAmount}`);
    }

    // Repeated key per id - how Spring binds a List<Integer> from the query string.
    if (filters?.paymentTypeIds?.length) {
      for (const paymentTypeId of filters.paymentTypeIds) {
        params = params.append('paymentTypeIds', `${paymentTypeId}`);
      }
    }

    return params;
  }

  /** The filters both calls share, so the count always describes the listed rows. */
  private applyFilters(params: HttpParams, filters: any, dateFrom: string, dateTo: string): HttpParams {
    if (filters?.matchStatus)    params = params.set('matchStatus', filters.matchStatus);
    if (filters?.reference)      params = params.set('reference', filters.reference);
    if (filters?.paymentDetails) params = params.set('paymentDetails', filters.paymentDetails);
    if (filters?.invoiceNumber)  params = params.set('invoiceNumber', filters.invoiceNumber);
    if (filters?.beneficiaryBankName) params = params.set('beneficiaryBankName', filters.beneficiaryBankName);
    if (filters?.paymentType)    params = params.set('paymentType', filters.paymentType);
    if (dateFrom)                params = params.set('dateFrom', dateFrom);
    if (dateTo)                  params = params.set('dateTo', dateTo);

    // Guarded on null/'' rather than on truthiness - 0 is a legitimate statement amount
    // and a plain if would silently drop it.
    if (filters?.amount !== null && filters?.amount !== undefined && filters?.amount !== '') {
      params = params.set('amount', `${filters.amount}`);
    }

    return params;
  }
}
